/**
 * Svea settlement exceptions (#2811).
 *
 * Unknown settled payments (Svea sent a paymentId we do not have) and paid
 * requests that have not appeared in a settlement after the configured delay.
 */

import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { formatCentsForDisplay as formatPrice } from '@/utils/money';
import {
  useSveaSettlementExceptions,
  type PaidUnsettledPayment,
  type UnknownSettledPayment,
} from '@/hooks/useSveaSettlements';

export function SveaSettlementExceptions() {
  const { t } = useTranslation('invoices');
  const { t: tc } = useTranslation('common');
  const query = useSveaSettlementExceptions();
  const unknownMethod = t('sveaSettlements.methodUnknown');
  const deliveryLabels = {
    sent: t('sveaSettlements.deliveryInfoSent'),
    failed: t('sveaSettlements.deliveryInfoFailed'),
    pending: t('sveaSettlements.deliveryInfoPending'),
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, sm: 3 } }} data-testid="svea-settlement-exceptions">
      <Typography variant="h4" fontWeight={600} gutterBottom>
        {t('sveaSettlements.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('sveaSettlements.subtitle', { days: query.data?.unsettledAfterDays ?? 5 })}
      </Typography>

      {query.isPending ? (
        <CircularProgress size={24} />
      ) : query.isError ? (
        <Typography color="error">{tc('error')}</Typography>
      ) : (
        <Stack spacing={3}>
          <ExceptionTable
            title={t('sveaSettlements.unknownTitle')}
            empty={t('sveaSettlements.unknownEmpty')}
            testId="svea-settlement-unknown"
            columns={[
              t('sveaSettlements.paymentId'),
              t('sveaSettlements.orderNumber'),
              t('sveaSettlements.gross'),
              t('sveaSettlements.refunded'),
              t('sveaSettlements.commission'),
              t('sveaSettlements.method'),
            ]}
            rows={(query.data?.unknownSettled ?? []).map((row) => unknownCells(row, unknownMethod))}
          />
          <ExceptionTable
            title={t('sveaSettlements.unsettledTitle')}
            empty={t('sveaSettlements.unsettledEmpty')}
            testId="svea-settlement-unsettled"
            columns={[
              t('sveaSettlements.paymentId'),
              t('sveaSettlements.invoice'),
              t('sveaSettlements.amount'),
              t('sveaSettlements.status'),
              t('sveaSettlements.deliveryInfo'),
            ]}
            rows={(query.data?.paidUnsettled ?? []).map((row) =>
              unpaidCells(row, deliveryInfoText(row, deliveryLabels)),
            )}
          />
        </Stack>
      )}
    </Box>
  );
}

function unknownCells(row: UnknownSettledPayment, unknownMethod: string): string[] {
  return [
    row.paymentId,
    row.orderNumber,
    formatPrice(row.sellerGrossCents),
    formatPrice(row.refundedCents),
    formatPrice(row.commissionCents),
    row.paymentMethod ?? unknownMethod,
  ];
}

function deliveryInfoText(
  row: PaidUnsettledPayment,
  labels: { sent: string; failed: string; pending: string },
): string {
  if (row.deliveryInfoStatus === 'sent') return labels.sent;
  if (row.deliveryInfoStatus === 'failed') {
    return row.deliveryInfoError ? `${labels.failed}: ${row.deliveryInfoError}` : labels.failed;
  }
  return labels.pending;
}

function unpaidCells(row: PaidUnsettledPayment, deliveryInfo: string): string[] {
  return [row.pmtId, row.invoiceId, formatPrice(row.amountCents), row.status, deliveryInfo];
}

function ExceptionTable({
  title,
  empty,
  testId,
  columns,
  rows,
}: {
  title: string;
  empty: string;
  testId: string;
  columns: string[];
  rows: string[][];
}) {
  return (
    <Paper sx={{ p: 2 }} data-testid={testId}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Chip size="small" label={String(rows.length)} />
      </Stack>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {empty}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((cells) => (
              <TableRow key={cells.join('|')}>
                {cells.map((cell, index) => (
                  <TableCell key={`${cells[0] ?? 'row'}-${String(index)}`}>{cell}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}
